import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Button } from '../../Button';
import { Input } from '../../Input';
import Loader from '../../Loader';
import Select from '../../Select';
import { missingVariableHintKeys } from '../variableSourceLabelKeys';
import { useIssueContractModalController } from './useIssueContractModalController';

export default function IssueContractModal() {
  const {
    t,
    step,
    templateId,
    contractorId,
    issuanceCity,
    customValues,
    templateError,
    contractorError,
    selectedTemplate,
    customFields,
    templateOptions,
    contractorOptions,
    saleId,
    saleOptions,
    hasMultipleSales,
    preview,
    missingByKey,
    hasMissingVariables,
    missingLegalFields,
    hasMissingAddress,
    legalValues,
    handleLegalValueChange,
    handleSaveMissingLegalInfo,
    isSavingLegalInfo,
    missingAnchorNames,
    hasMissingAnchors,
    isLoadingOptions,
    hasActiveTemplates,
    hasContractors,
    isPreviewing,
    isIssuing,
    handleTemplateChange,
    handleContractorChange,
    handleSaleChange,
    handleIssuanceCityChange,
    handleCustomValueChange,
    handleContinue,
    handleBack,
    handleIssue,
    handleCloseModal,
  } = useIssueContractModalController();

  if (isIssuing) {
    return (
      <div className="my-4 flex w-full flex-col items-center gap-3 px-1 py-8 text-center">
        <Loader className="h-10 w-10" />
        <strong className="text-text-primary">
          {t('contract.issue.modal.issuing')}
        </strong>
        <p className="text-text-secondary text-sm">
          {t('contract.issue.modal.issuingHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="my-4 flex max-h-[65vh] w-full flex-col gap-4 overflow-y-auto px-1">
      {step === 'form' && (
        <>
          {isLoadingOptions && (
            <div className="flex justify-center py-6">
              <Loader className="h-8 w-8" />
            </div>
          )}

          {!isLoadingOptions && (
            <>
              {!hasActiveTemplates && (
                <p className="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                  {t('contract.issue.modal.noActiveTemplates')}
                </p>
              )}

              {!hasContractors && (
                <p className="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                  {t('contract.issue.modal.noContractors')}
                </p>
              )}

              <Select
                placeholder={t('contract.issue.modal.templateField')}
                options={templateOptions}
                value={templateId}
                error={templateError}
                disabled={!hasActiveTemplates}
                onChange={handleTemplateChange}
              />

              <Select
                placeholder={t('contract.issue.modal.contractorField')}
                options={contractorOptions}
                value={contractorId}
                error={contractorError}
                disabled={!hasContractors}
                onChange={handleContractorChange}
              />

              {hasMultipleSales && (
                <Select
                  placeholder={t('contract.issue.modal.saleField')}
                  options={saleOptions}
                  value={saleId}
                  emptyOption
                  emptyOptionLabel={t('contract.issue.modal.saleFieldLatest')}
                  onChange={handleSaleChange}
                />
              )}

              <Input
                type="text"
                name="issuanceCity"
                placeholder={t('contract.issue.modal.issuanceCityField')}
                value={issuanceCity}
                onChange={(event) =>
                  handleIssuanceCityChange(event.target.value)
                }
              />

              {hasMissingAnchors && (
                <p className="flex items-start gap-2 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                  <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />
                  {t('contract.issue.modal.missingAnchorsWarning', {
                    names: missingAnchorNames,
                  })}
                </p>
              )}

              {customFields.length > 0 && (
                <section className="flex flex-col gap-4">
                  <h3 className="text-text-primary text-sm font-semibold">
                    {t('contract.issue.modal.customValuesTitle')}
                  </h3>
                  {customFields.map((variable) => (
                    <Input
                      key={variable.key}
                      type="text"
                      name={`custom-value-${variable.key}`}
                      placeholder={variable.label}
                      value={customValues[variable.key] ?? ''}
                      onChange={(event) =>
                        handleCustomValueChange(
                          variable.key,
                          event.target.value,
                        )
                      }
                    />
                  ))}
                </section>
              )}
            </>
          )}

          <section className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
            <Button
              className="w-full sm:w-50"
              labelPath="contract.issue.modal.cancel"
              color="ghost"
              disabled={isPreviewing}
              onClick={handleCloseModal}
            />
            <Button
              className="w-full"
              labelPath="contract.issue.modal.next"
              disabled={isLoadingOptions || !hasActiveTemplates || isPreviewing}
              isLoading={isPreviewing}
              onClick={handleContinue}
            />
          </section>
        </>
      )}

      {step === 'preview' && preview && selectedTemplate && (
        <>
          <div className="flex flex-col gap-1">
            <h3 className="text-text-primary text-sm font-semibold">
              {t('contract.issue.modal.previewTitle')}
            </h3>
            <p className="text-text-secondary text-sm">
              {t('contract.issue.modal.previewDescription')}
            </p>
          </div>

          <ul className="flex flex-col gap-2">
            {selectedTemplate.variablesSchema.map((variable) => {
              const missingSource = missingByKey.get(variable.key);
              const resolvedValue = preview.resolved[variable.key];

              return (
                <li
                  key={variable.key}
                  className={
                    missingSource
                      ? 'flex flex-col gap-0.5 rounded-lg border border-red-300 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20'
                      : 'border-border-default bg-surface-elevated flex flex-col gap-0.5 rounded-lg border p-2'
                  }
                >
                  <span
                    className={
                      missingSource
                        ? 'text-xs font-medium text-red-700 dark:text-red-300'
                        : 'text-text-tertiary text-xs'
                    }
                  >
                    {variable.label}
                  </span>
                  {!missingSource && (
                    <strong className="text-text-primary text-sm break-all">
                      {resolvedValue || t('contract.issue.modal.emptyValue')}
                    </strong>
                  )}
                  {missingSource && (
                    <span className="flex items-center gap-1 text-sm text-red-700 dark:text-red-300">
                      <ExclamationTriangleIcon className="size-4 shrink-0" />
                      {t(missingVariableHintKeys[missingSource])}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {hasMissingVariables && (
            <p className="text-sm font-medium text-red-700 dark:text-red-300">
              {t('contract.issue.modal.missingTitle')}
            </p>
          )}

          {missingLegalFields.length > 0 && (
            <section className="border-border-default flex flex-col gap-3 rounded-lg border p-3">
              <div className="flex flex-col gap-1">
                <h3 className="text-text-primary text-sm font-semibold">
                  {t('contract.issue.modal.fillLegalTitle')}
                </h3>
                <p className="text-text-secondary text-xs">
                  {t('contract.issue.modal.fillLegalHint')}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {missingLegalFields.map((field) => (
                  <Input
                    key={field}
                    type="text"
                    name={`legal-${field}`}
                    placeholder={t(`contract.legalInfo.fields.${field}`)}
                    value={legalValues[field] ?? ''}
                    disabled={isSavingLegalInfo}
                    onChange={(event) =>
                      handleLegalValueChange(field, event.target.value)
                    }
                  />
                ))}
              </div>

              <Button
                className="w-full sm:w-auto sm:self-end"
                color="outline"
                labelPath="contract.issue.modal.fillLegalSave"
                isLoading={isSavingLegalInfo}
                onClick={handleSaveMissingLegalInfo}
              />
            </section>
          )}

          {hasMissingAddress && (
            <p className="flex items-start gap-2 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
              <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />
              {t('contract.issue.modal.missingAddressHint')}
            </p>
          )}

          <section className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
            <Button
              className="w-full sm:w-50"
              labelPath="contract.issue.modal.back"
              color="ghost"
              onClick={handleBack}
            />
            <Button
              className="w-full"
              labelPath="contract.issue.modal.confirm"
              disabled={hasMissingVariables}
              onClick={handleIssue}
            />
          </section>
        </>
      )}
    </div>
  );
}
